import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    TextField,
    Stack,
    Button,
    LinearProgress,
    Box,
    Alert,
} from '@mui/material';
import { FileSelectorComponent, ReactHlsPlayer } from 'ui';
import { useApi } from 'hooks';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { components } from 'apis';
type GetPresignedURLImageLinkResponse =
    components['schemas']['GetVideoUploadLinkResponse'];
type Media = components['schemas']['Media'];
interface UploadVideoProps {
    channelId: string;
    title: string;
    setTitle: Dispatch<SetStateAction<string>>;
    setVideoId: Dispatch<SetStateAction<string>>;
    setNextEnabled: (index: number) => void;
    index: number;
}

const UploadVideo: React.FC<UploadVideoProps> = (props) => {
    const [file, setFile] = useState<File>();
    const { api } = useApi();
    const [filePath, setFilePath] = useState<string>('');
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploaded, setUploaded] = useState<boolean>(false);
    const [progress] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const addMedia = api.useMutation('post', '/media');
    useEffect(() => {
        if (file) {
            setFilePath(URL.createObjectURL(file));
        }
    }, [file]);
    const submit = async () => {
        if (file) {
            setErrorMessage('');
            setUploading(true);
            setUploaded(false);
            const videoData = new FormData();
            videoData.append('file', file);
            videoData.append('path', `channel/${props.channelId}/videos`);
            videoData.append('name', props.title);
            const response: Media = await addMedia.mutateAsync({
                body: {
                    title: props.title,
                    tags: [],
                    thumbnail: '',
                    description: '',
                    tier: 'FREE',
                    mediaType: 'VIDEO',
                    audience: 'UNLISTED',
                    restricted: true,
                },
            });
            if (response) {
                videoData.append('videoId', response.id);
                props.setVideoId(response.id);
                try {
                    const urlResponse = await fetch(
                        `/api/v1/media/${response.id}/videoUploadLink?` +
                            new URLSearchParams({
                                path: props.title,
                                fileName: file.name,
                                fileType: file.type,
                                title: response.title,
                            }).toString(),
                        {
                            method: 'GET',
                        },
                    );
                    const res = await urlResponse.json();
                    console.log('uploading');
                    await fetch(res.url, {
                        method: 'PUT',
                        body: file,
                        headers: {
                            ContentType: file.type,
                            ContentLength: file.size.toString(),
                        },
                    });
                    console.log('done');
                    setUploaded(true);
                    setUploading(false);
                    props.setNextEnabled(props.index);
                } catch {
                    setErrorMessage('Unable to upload file');
                }
            } else {
                setErrorMessage(
                    'Unknown error occurred while creating your video.  Please try again',
                );
                setUploading(false);
            }
        }
    };
    return (
        <Stack direction="column" alignItems="center" spacing={2}>
            {uploaded && errorMessage === '' && (
                <Alert severity="success">Successfully uploaded video</Alert>
            )}
            {uploaded && errorMessage !== '' && (
                <Alert severity="error">{errorMessage}</Alert>
            )}
            <TextField
                required
                disabled={uploaded && errorMessage === ''}
                id="outlined-required"
                label="Title"
                value={props.title}
                onChange={(e: { target: { value: string } }) =>
                    props.setTitle(e.target.value)
                }
            />
            <FileSelectorComponent
                fileType="video/*"
                id="upload-video-id"
                width="25%"
                height="100%"
                title="Select video"
                setCurrentFile={setFile}
                disabled={uploaded && errorMessage === ''}
            />
            {file !== undefined && (
                <ReactHlsPlayer
                    hls={false}
                    style={{ width: '640px', height: '480px' }}
                    src={filePath}
                    autoPlay={false}
                />
            )}
            {file && (
                <Button
                    variant="contained"
                    disabled={
                        uploading ||
                        props.title === '' ||
                        (uploaded && errorMessage === '')
                    }
                    onClick={submit}
                >
                    Upload Video
                </Button>
            )}
            <Box sx={{ width: '100%' }}>
                {(uploading || uploaded) && (
                    <LinearProgress
                        variant="determinate"
                        color={
                            uploaded
                                ? errorMessage === ''
                                    ? 'success'
                                    : 'error'
                                : 'primary'
                        }
                        value={progress}
                    />
                )}
            </Box>
        </Stack>
    );
};

export default UploadVideo;
